import { useState, useEffect } from "react";
import { Button } from "./ui-common/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui-common/card";
import { BookOpen, LogOut, LifeBuoy, BookText, GraduationCap, Shield } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui-common/tooltip";
import axiosInstance from './axiosInstance';
import LoadingScreen from './ui-common/LoadingScreen';
import ImpersonationButton from './ImpersonationButton';

const CoursesPage = ({ user, onCourseSelect, onLogout, onNavigateToSupport, onNavigateToInstructor }) => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [instructorCourses, setInstructorCourses] = useState([]);
  useEffect(() => {
    fetchCourses();
    fetchInstructorCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/courses');
      
      const coursesData = response.data;
      setCourses(coursesData);
    } catch (err) {
      console.error('Error fetching courses:', err);
      if (err.response?.status === 401) {
        setError('Please sign in to view your courses');
      } else {
        setError(err.message || 'Failed to fetch courses');
      }
    } finally {
      setLoading(false);
    }
  };

    const fetchInstructorCourses = async () => {
    try {
      const response = await axiosInstance.get('/api/instructor_courses');
      setInstructorCourses(response.data || []);
    } catch (err) {
      console.error('Error fetching instructor courses:', err);
      setInstructorCourses([]);
    }
  };

  const handleEnterCourse = (course) => {
    onCourseSelect(course);
  };

  const handleLogout = () => {
    onLogout();
  };

  if (loading) {
    return <LoadingScreen icon={<BookOpen />} text="Loading your courses..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-destructive mb-4">Error: {error}</p>
          <Button onClick={fetchCourses} variant="outline">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
    <div className="min-h-screen bg-background">
      {/* Fixed Header */}
      <div className="sticky top-0 z-10 bg-muted border-b backdrop-blur-sm bg-muted/95">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground">
                My Courses
              </h1>
              <p className="text-muted-foreground mt-1">
                Welcome back, {user?.name || 'Student'}! Here are your enrolled courses.
              </p>
              <div className="mt-2 flex items-center gap-4">
                <span className="text-sm bg-primary/10 text-primary px-3 py-1 rounded-full">
                  Current Semester
                </span>
              </div>
            </div>
            <div className="flex gap-1 items-center">
              <ImpersonationButton user={user} />
              {instructorCourses.length > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline" size="default" className="gap-2" onClick={onNavigateToInstructor}>
                      <GraduationCap className="h-4 w-4" />
                      Instructor
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Go to Instructor Dashboard</p>
                  </TooltipContent>
                </Tooltip>
              )}
              <div className="flex gap-0.5 items-center">
                {/* <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline" size="icon">
                      <BookText className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>User Manual</p>
                  </TooltipContent>
                </Tooltip> */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={onNavigateToSupport}
                    >
                      <LifeBuoy className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Support & Feedback</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="icon" onClick={handleLogout}>
                    <LogOut className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Logout</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="max-w-6xl mx-auto p-6 pt-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map((course) => (
            <Card key={course.name} className="hover:shadow-lg transition-shadow flex flex-col h-full">
              <CardHeader className="flex-shrink-0">
                <CardTitle className="text-lg line-clamp-2">
                  {course.label} - {course.id}
                  {course.staff_only && (
                    <span className="inline-flex items-center gap-1 ml-2 text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200 px-2 py-0.5 rounded-full align-middle">
                      <Shield className="h-3 w-3" />
                      Staff Only
                    </span>
                  )}
                </CardTitle>
                <CardDescription className="text-sm">
                  {course.year && (
                    <span className="font-medium text-foreground/70">
                      {course.year} • {course.semester === 0 ? 'Yearly' : course.semester === 1 ? 'Semester A' : 'Semester B'}
                      {' · '}
                    </span>
                  )}
                  Study Buddy is available for this course.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col flex-1">
                {/*<p className="text-sm text-muted-foreground mb-4 flex-1 line-clamp-3">
                   Get instant answers, explore course materials, and receive personalized AI assistance for your studies.
                </p> */}
                <Button 
                  onClick={() => handleEnterCourse(course)}
                  className="w-full mt-auto bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  Enter Course
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {courses.length === 0 && (
          <div className="text-center py-12">
            <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground">
              No Courses Enrolled
            </h3>
            <p className="text-muted-foreground">
              Contact your instructor to get enrolled in courses.
            </p>
          </div>
        )}
      </div>
    </div>

    </TooltipProvider>
  );
};

export default CoursesPage;